import React, { useState, useEffect } from 'react';
import { 
  Users, Building, MessageSquareWarning, Wallet, Bell, 
  Calendar, CheckCircle, Clock, XCircle, Search, Plus, FileText,
  HandHeart, BarChart2, Sun, CloudSun, CloudFog, CloudRain, CloudSnow, CloudLightning, MapPin,
  TrendingUp, PieChart, ChevronDown
} from 'lucide-react';
import styles from './AdminDashboard.module.css';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ fullName: 'Admin' });
  const [data, setData] = useState({
    totalResidents: 0,
    totalFlats: 0,
    pendingComplaints: 0,
    complaintStats: { pending: 0, inProgress: 0, resolved: 0, rejected: 0, total: 0 },
    notices: [],
    visitors: [],
    financialStats: { totalReceived: 0, totalExpected: 0 },
    dailyCollectionData: []
  });
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState({ temp: 32, condition: 'Partly Cloudy', city: 'Indore, India', icon: <Sun size={18} /> });

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const lat = 22.7196;
        const lon = 75.8577;
        const city = 'Indore';
        const country = 'India';

        const weatherRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
        const weatherData = await weatherRes.json();
        
        const temp = Math.round(weatherData.current_weather.temperature);
        const code = weatherData.current_weather.weathercode;
        
        let condition = 'Clear';
        let icon = <Sun size={32} />;
        
        if (code === 0) { condition = 'Clear sky'; icon = <Sun size={18} />; }
        else if (code === 1 || code === 2 || code === 3) { condition = 'Partly cloudy'; icon = <CloudSun size={18} />; }
        else if (code === 45 || code === 48) { condition = 'Fog'; icon = <CloudFog size={18} />; }
        else if (code >= 51 && code <= 67) { condition = 'Rain'; icon = <CloudRain size={18} />; }
        else if (code >= 71 && code <= 82) { condition = 'Snow'; icon = <CloudSnow size={18} />; }
        else if (code >= 95) { condition = 'Thunderstorm'; icon = <CloudLightning size={18} />; }
        
        setWeather({ temp, condition, city: `${city}, ${country}`, icon });
      } catch (err) {
        console.error("Weather fetch failed", err);
      }
    };
    
    fetchWeather();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch user from local storage
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo && userInfo.fullName) {
          setUser(userInfo);
        }

        const [residentsRes, complaintsRes, noticesRes, visitorsRes, maintenanceStatsRes] = await Promise.all([
          api.get('/admin/residents'),
          api.get('/admin/complaints'),
          api.get('/community/notices'),
          api.get('/security/visitors'),
          api.get('/maintenance/stats')
        ]);

        const residents = residentsRes.data;
        const complaints = complaintsRes.data;
        
        // Calculate Flats (unique flat numbers)
        const flatSet = new Set();
        residents.forEach(r => {
           if (r.flatNumber) flatSet.add(`${r.wing || ''}-${r.flatNumber}`);
        });

        // Calculate Complaint Stats
        let pending = 0, inProgress = 0, resolved = 0, rejected = 0;
        complaints.forEach(c => {
          if (c.status === 'Pending' || c.status === 'Open') pending++;
          else if (c.status === 'In Progress') inProgress++;
          else if (c.status === 'Resolved' || c.status === 'Closed') resolved++;
          else if (c.status === 'Rejected') rejected++;
        });

        setData({
          totalResidents: residents.length,
          totalFlats: flatSet.size,
          pendingComplaints: pending,
          complaintStats: { pending, inProgress, resolved, rejected, total: complaints.length },
          financialStats: { 
            totalReceived: maintenanceStatsRes.data.totalReceived || 0,
            totalExpected: maintenanceStatsRes.data.totalExpected || 0
          },
          dailyCollectionData: maintenanceStatsRes.data.dailyCollectionData || [],
          notices: noticesRes.data.slice(0, 3).map(n => ({
            title: n.title,
            date: new Date(n.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            meetingDateTime: n.meetingDateTime,
            meetingLocation: n.meetingLocation
          })),
          visitors: visitorsRes.data.slice(0, 4).map(v => ({
            name: v.name,
            flat: v.flatNumber ? `${v.wing || ''}-${v.flatNumber}` : 'N/A',
            time: v.entryTime ? new Date(v.entryTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                 (v.createdAt ? new Date(v.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'),
            image: v.name.substring(0, 2).toUpperCase()
          }))
        });
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch admin dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const firstName = user.fullName ? user.fullName.split(" ")[0] : "Admin";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    if (hour < 21) return 'Good Evening';
    return 'Good Night';
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  const stats = [
    { title: 'Total Residents', value: data.totalResidents, icon: Users, type: 'green', actionText: 'View all', link: '/admin/residents' },
    { title: 'Total Flats', value: data.totalFlats, icon: Building, type: 'orange', actionText: 'View all', link: '/admin/residents' },
    { title: 'Pending Complaints', value: data.pendingComplaints, icon: MessageSquareWarning, type: 'orange', actionText: 'View all', link: '/admin/complaints' },
    { title: 'Total Collection', value: `₹${data.financialStats.totalReceived.toLocaleString('en-IN')}`, icon: Wallet, type: 'green', actionText: 'View report', link: '/admin/maintenance' },
  ];

  return (
    <div className={styles.dashboard}>
      <main className={styles.mainContent}>
        {/* WELCOME BANNER */}
        <div className={styles.welcomeBanner}>
          <div className={styles.bannerContent}>
            <div className={styles.welcomeProfileGroup}>
              <div>
                <h2 className={styles.welcomeHeading}>
                  {getGreeting()}, {firstName}! <HandHeart size={28} color="#f59e0b" className={styles.greetingIcon} />
                </h2>
                <p>Here's what's happening in Sanskriti Shubh Aangan today.</p>
              </div>
            </div>
            <div className={styles.weatherInfo}>
              <span>{weather.icon} {weather.temp}°C • {weather.condition} • {weather.city}</span>
            </div>
          </div>
        </div>

        {/* STATS ROW */}
        <div className={styles.statsGrid}>
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className={styles.statCard}>
                <div className={styles.statTop}>
                  <span className={styles.statTitle}>{stat.title}</span>
                  <div className={`${styles.statIcon} ${styles[stat.type]}`}>
                    <Icon size={16} />
                  </div>
                </div>
                <div className={styles.statBottom}>
                  <strong>{stat.value}</strong>
                  <button className={styles.viewLink} onClick={() => navigate(stat.link)}>{stat.actionText}</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* MIDDLE ROW */}
        <div className={styles.middleGrid}>
          {/* Complaints Overview */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Complaints Overview</h3>
              <button className={styles.viewAll} onClick={() => navigate('/admin/complaints')}>View All</button>
            </div>
            <div className={styles.complaintsContent}>
              <div className={styles.donutChart}>
                <svg viewBox="0 0 36 36" className={styles.circularChart}>
                  <path className={styles.circleBg} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={styles.circleOrange} strokeDasharray="30, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={styles.circleBlue} strokeDasharray="25, 100" strokeDashoffset="-30" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={styles.circleGreen} strokeDasharray="20, 100" strokeDashoffset="-55" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={styles.circleRed} strokeDasharray="5, 100" strokeDashoffset="-75" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div className={styles.donutText}>
                  <strong>{data.complaintStats.total}</strong>
                  <span>Total</span>
                </div>
              </div>
              <div className={styles.legend}>
                <div className={styles.legendItem}><span className={styles.dotOrange}></span> {data.complaintStats.pending} Pending</div>
                <div className={styles.legendItem}><span className={styles.dotBlue}></span> {data.complaintStats.inProgress} In Progress</div>
                <div className={styles.legendItem}><span className={styles.dotGreen}></span> {data.complaintStats.resolved} Resolved</div>
                <div className={styles.legendItem}><span className={styles.dotRed}></span> {data.complaintStats.rejected} Rejected</div>
              </div>
            </div>
          </div>

          {/* Recent Notices */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Recent Notices</h3>
              <button className={styles.viewAll} onClick={() => navigate('/admin/notices')}>View All</button>
            </div>
            <div className={styles.list}>
              {data.notices.length === 0 ? (
                <div className={styles.emptyList}>No recent notices</div>
              ) : data.notices.map((n, i) => (
                <div key={i} className={styles.listItem}>
                  <div className={styles.listIconNotice}><Bell size={14}/></div>
                  <div className={styles.listText}>
                    <h4>{n.title}</h4>
                    <p>{n.date}</p>
                    {(n.meetingDateTime || n.meetingLocation) && (
                      <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b', marginTop: '4px', fontWeight: '500' }}>
                        {n.meetingDateTime && <span><Calendar size={11} style={{marginRight: '2px', verticalAlign: 'text-bottom'}} /> {new Date(n.meetingDateTime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                        {n.meetingLocation && <span><MapPin size={11} style={{marginRight: '2px', verticalAlign: 'text-bottom'}} /> {n.meetingLocation}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Today's Visitors */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Today's Visitors</h3>
              <button className={styles.viewAll} onClick={() => navigate('/admin/visitors')}>View All</button>
            </div>
            <div className={styles.list}>
              {data.visitors.length === 0 ? (
                <div className={styles.emptyList}>No visitors today</div>
              ) : data.visitors.map((v, i) => (
                <div key={i} className={styles.listItem}>
                  <div className={styles.avatarSmall}>{v.image}</div>
                  <div className={styles.listText}>
                    <h4>{v.name}</h4>
                    <p>{v.flat}</p>
                  </div>
                  <span className={styles.time}>{v.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className={styles.bottomGrid}>
          {/* Financial Overview */}
          <div className={`${styles.card} ${styles.financialCardFull}`}>
            <div className={styles.finHeaderRow}>
              <div className={styles.finHeaderLeft}>
                <div className={styles.finHeaderIcon}><Wallet size={20} color="#154530" /></div>
                <div>
                  <h3>Financial Overview</h3>
                  <p>Track your collection progress</p>
                </div>
              </div>
              <div className={styles.finHeaderRight}>
                <button className={styles.finDropdownBtn}>
                  Last 30 Days <ChevronDown size={14} />
                </button>
              </div>
            </div>

            <div className={styles.finStatsRow}>
              <div className={styles.finTotalBlock}>
                <span>Total Collection</span>
                <div className={styles.finTotalAmount}>
                  <strong>₹{data.financialStats.totalReceived.toLocaleString('en-IN')}</strong>
                  <span className={styles.finTotalPercent}>0% of expected</span>
                </div>
                <div className={styles.finBadge}>
                  <span className={styles.finDot}></span> On Track
                </div>
              </div>
              <div className={styles.finExpectedBlock}>
                <div className={styles.finExpectedIcon}><TrendingUp size={16} color="#22c55e" /></div>
                <div className={styles.finExpectedTexts}>
                  <span>Expected Amount</span>
                  <strong>₹{data.financialStats.totalExpected.toLocaleString('en-IN')}</strong>
                  <small>This Period</small>
                </div>
              </div>
            </div>

            <div className={styles.chartWrapper} style={{ height: '300px', width: '100%', marginTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.dailyCollectionData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#64748b', fontSize: 12 }} 
                    dy={10} 
                    minTickGap={20}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tickFormatter={(value) => `₹${value>=1000 ? (value/1000)+'k' : value}`} 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    dx={-10}
                  />
                  <Tooltip 
                    formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Collection']}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#22c55e" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorAmount)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className={styles.finSummaryCards}>
              <div className={styles.finSummaryCard}>
                <div className={`${styles.finSummaryIcon} ${styles.bgGreen}`}><Wallet size={20} color="#15803d" /></div>
                <div className={styles.finSummaryText}>
                  <span>Collected</span>
                  <strong>₹{data.financialStats.totalReceived.toLocaleString('en-IN')}</strong>
                </div>
              </div>
              <div className={styles.finSummaryCard}>
                <div className={`${styles.finSummaryIcon} ${styles.bgBlue}`}><Calendar size={20} color="#2563eb" /></div>
                <div className={styles.finSummaryText}>
                  <span>Expected</span>
                  <strong>₹{data.financialStats.totalExpected.toLocaleString('en-IN')}</strong>
                </div>
              </div>
              <div className={styles.finSummaryCard}>
                <div className={`${styles.finSummaryIcon} ${styles.bgPurple}`}><PieChart size={20} color="#7e22ce" /></div>
                <div className={styles.finSummaryText}>
                  <span>Pending</span>
                  <strong>₹{(data.financialStats.totalExpected - data.financialStats.totalReceived > 0 ? data.financialStats.totalExpected - data.financialStats.totalReceived : 0).toLocaleString('en-IN')}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Quick Actions</h3>
            </div>
            <div className={styles.quickActionsGrid}>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/notices')}>
                <span className={styles.qaIcon}><Bell size={14} color="#e88835" /></span> Add Notice
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/events')}>
                <span className={styles.qaIcon}><Calendar size={14} color="#e88835" /></span> Add Event
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/residents')}>
                <span className={styles.qaIcon}><Users size={14} color="#e88835" /></span> Add Resident
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/security-logs')}>
                <span className={styles.qaIcon}><Users size={14} color="#e88835" /></span> Add Staff
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/reports')}>
                <span className={styles.qaIcon}><BarChart2 size={14} color="#3b82f6" /></span> Reports
              </button>
              <button className={styles.qaBtn} onClick={() => navigate('/admin/notifications')}>
                <span className={styles.qaIcon}><Bell size={14} color="#e88835" /></span> Send Notification
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
